import React, { useState, useEffect } from "react";
import { Download, Loader2, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiService } from "../../services";

interface Sample {
  id: string;
  title: string;
  description: string;
  tier: string;
  status: string;
  screenshot: string;
  tech_stack: {
    backend: string[];
    frontend: string[];
    deployment: string[];
  };
  features: string[];
  estimated_cost: Record<string, string>;
  size_mb: number;
  docs_url: string;
}

interface SamplesData {
  samples: Sample[];
}

interface UserDownload {
  id: string;
  type: string;
  fileName: string;
}

interface PremiumSamplesProps {
  hasActiveSubscription: boolean;
  customerTier: string;
  onPurchaseClick: (tier: string) => void;
  onLoginClick: () => void;
  downloads?: UserDownload[];
}

const GITHUB_RAW_URL =
  "https://raw.githubusercontent.com/victordibia/designing-multiagent-systems/main/premium-samples/samples.json";
const GITHUB_SCREENSHOT_BASE =
  "https://raw.githubusercontent.com/victordibia/designing-multiagent-systems/main/premium-samples";

const PremiumSamples: React.FC<PremiumSamplesProps> = ({
  hasActiveSubscription,
  customerTier,
  onPurchaseClick,
  onLoginClick,
  downloads = [],
}) => {
  const { t } = useTranslation("home");
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    fetchSamples();
  }, []);

  const fetchSamples = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(GITHUB_RAW_URL);
      if (!response.ok) throw new Error("Failed to fetch samples");
      const data: SamplesData = await response.json();
      setSamples(data.samples);
    } catch (err) {
      setError("Unable to load samples. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (mb: number): string => {
    return `${mb} MB`;
  };

  const findDownloadForSample = (sampleId: string): UserDownload | undefined => {
    return downloads.find(
      (d) => d.type === "samplecode" && d.fileName.startsWith(`${sampleId}-`)
    );
  };

  const handleDownload = async (sample: Sample) => {
    const download = findDownloadForSample(sample.id);
    if (!download) return;

    try {
      setDownloading(sample.id);
      const url = await apiService.getDownloadUrl(download.id);
      const link = document.createElement("a");
      link.href = url;
      link.download = download.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(null);
    }
  };

  const getActionButton = (sample: Sample) => {
    const hasProfessionalAccess =
      customerTier === "professional" || customerTier === "enterprise";

    if (!hasActiveSubscription) {
      return (
        <button
          onClick={onLoginClick}
          className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-accent hover:bg-accent/90 rounded-lg transition-colors"
        >
          {t("premiumSamples.signInToDownload")}
        </button>
      );
    }

    if (!hasProfessionalAccess) {
      return (
        <button
          onClick={() => onPurchaseClick("professional")}
          className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-accent hover:bg-accent/90 rounded-lg transition-colors"
        >
          {t("premiumSamples.upgradeToProfessional")}
        </button>
      );
    }

    const download = findDownloadForSample(sample.id);
    const isDownloading = downloading === sample.id;

    return (
      <div className="flex flex-col gap-2">
        <button
          onClick={() => handleDownload(sample)}
          disabled={!download || isDownloading}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-accent hover:bg-accent/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isDownloading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {isDownloading
            ? t("premiumSamples.downloading", "Downloading...")
            : t("premiumSamples.download")}
        </button>
        <p className="text-xs text-tertiary">
          {t("premiumSamples.downloadNote")}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
        <span className="ml-3 text-secondary text-sm">{t("premiumSamples.loading")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div className="ml-3">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            <button
              onClick={fetchSamples}
              className="mt-2 text-sm font-medium text-red-600 dark:text-red-400 hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (samples.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-primary mb-2">
          {t("premiumSamples.title")}
        </h2>
        <p className="text-sm text-secondary">
          {t("premiumSamples.description")}
        </p>
      </div>

      {/* Samples */}
      <div className="space-y-6">
        {samples.map((sample) => (
          <div
            key={sample.id}
            className="border border-tertiary rounded-lg overflow-hidden hover:border-accent/30 transition-colors"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              {/* Screenshot */}
              <div className="lg:col-span-1 bg-secondary/5">
                <img
                  src={`${GITHUB_SCREENSHOT_BASE}/${sample.screenshot}`}
                  alt={sample.title}
                  className="w-full h-48 lg:h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>

              {/* Content */}
              <div className="lg:col-span-2 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-primary">
                      {sample.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-tertiary">
                      <span className="capitalize">{sample.status}</span>
                      <span>•</span>
                      <span>{formatFileSize(sample.size_mb)}</span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-secondary mb-4 line-clamp-2">
                  {sample.description}
                </p>

                {/* Tech Stack */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {[
                      ...sample.tech_stack.backend.slice(0, 2),
                      ...sample.tech_stack.frontend.slice(0, 2),
                    ].map((tech, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-1 rounded text-xs bg-secondary/10 text-secondary"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div className="mb-4">
                  <ul className="text-sm text-secondary space-y-1">
                    {sample.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-accent mr-2 flex-shrink-0">
                          •
                        </span>
                        <span className="line-clamp-1">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4">
                  {getActionButton(sample)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Note */}
      <div className="text-xs text-tertiary">
        {t("premiumSamples.comingSoon")}
      </div>
    </div>
  );
};

export default PremiumSamples;
